import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Mail, Lock, User, Phone, Loader2, CheckCircle2, AlertCircle,
  InboxIcon, Eye, EyeOff, Home, Building2, Briefcase, KeyRound, ChevronLeft,
} from "lucide-react";
import { motion } from "framer-motion";
import { PublicFooter } from "@/components/PublicFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useInvitationByToken, useAcceptInvitation } from "@/hooks/useInvitations";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { signUpSchema } from "@/lib/schemas";
import type { UserRole } from "@/types/database";
import { trackEvent } from "@/lib/analytics";

// ─── Constants ────────────────────────────────────────────────────────────────

const STEP_DETAILS  = 1;
const STEP_ROLE     = 2;
const STEP_PASSWORD = 3;

const STEP_META = {
  [STEP_DETAILS]:  { title: "Create your account",         sub: "Start with your basic details" },
  [STEP_ROLE]:     { title: "How will you use Stratakey?", sub: "Your role shapes your entire dashboard experience" },
  [STEP_PASSWORD]: { title: "Secure your account",         sub: "Almost done — choose a strong password" },
};

const ROLES: { value: UserRole; label: string; description: string; Icon: React.ElementType; accent: string }[] = [
  { value: "strata_admin",     label: "Strata Admin",      description: "Manage a strata corporation or condo building",    Icon: Building2, accent: "text-blue-600 bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800" },
  { value: "property_manager", label: "Property Manager",  description: "Oversee multiple properties on behalf of owners",  Icon: Briefcase, accent: "text-violet-600 bg-violet-50 dark:bg-violet-950/40 border-violet-200 dark:border-violet-800" },
  { value: "host",             label: "STR Host",          description: "Rent out your unit on Airbnb, Vrbo, or similar",  Icon: KeyRound,  accent: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800" },
  { value: "individual",       label: "Individual Owner",  description: "Own a property and manage your STR compliance",   Icon: Home,      accent: "text-amber-600 bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function pwStrength(pw: string) {
  if (!pw) return { score: 0, label: "", color: "" };
  let s = 0;
  if (pw.length >= 8)         s++;
  if (pw.length >= 12)        s++;
  if (/[A-Z]/.test(pw))      s++;
  if (/[0-9]/.test(pw))      s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  if (s <= 1) return { score: s, label: "Weak",   color: "bg-red-500" };
  if (s <= 3) return { score: s, label: "Fair",   color: "bg-amber-500" };
  return            { score: s, label: "Strong", color: "bg-green-500" };
}

function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null;
  return (
    <p className="flex items-center gap-1 text-[11px] text-destructive mt-1">
      <AlertCircle className="h-3 w-3 shrink-0" /> {msg}
    </p>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function SignUp() {
  const [searchParams] = useSearchParams();
  const inviteToken     = searchParams.get("invite") ?? searchParams.get("token") ?? null;
  const checkoutSession = searchParams.get("checkout_session");
  const checkoutPlan    = searchParams.get("plan");

  const [step, setStep]               = useState(STEP_DETAILS);
  const [firstName, setFirstName]     = useState("");
  const [lastName, setLastName]       = useState("");
  const [email, setEmail]             = useState("");
  const [phone, setPhone]             = useState("");
  const [role, setRole]               = useState<UserRole | "">("");
  const [password, setPassword]       = useState("");
  const [showPw, setShowPw]           = useState(false);
  const [loading, setLoading]         = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError]   = useState(false);
  const [registered, setRegistered]   = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [honeypot, setHoneypot]       = useState("");

  const { signUp } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const acceptInvitation = useAcceptInvitation();
  const { data: invitation } = useInvitationByToken(inviteToken);

  const roleIsLocked = !!invitation || !!checkoutSession;
  const activeSteps  = roleIsLocked ? [STEP_DETAILS, STEP_PASSWORD] : [STEP_DETAILS, STEP_ROLE, STEP_PASSWORD];

  useEffect(() => {
    if (invitation) {
      setEmail(invitation.email);
      setRole(invitation.role as UserRole);
    }
  }, [invitation]);

  useEffect(() => {
    if (checkoutPlan && !invitation && !role) {
      const valid: UserRole[] = ["individual", "strata_admin", "property_manager", "host"];
      if (valid.includes(checkoutPlan as UserRole)) setRole(checkoutPlan as UserRole);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutPlan]);

  // ── Navigation ──────────────────────────────────────────────────────────────

  const goNext = () => {
    setFieldErrors({});
    if (step === STEP_DETAILS) {
      const errs: Record<string, string> = {};
      if (!firstName.trim()) errs.firstName = "First name is required";
      if (!lastName.trim())  errs.lastName  = "Last name is required";
      if (!email.trim())     errs.email     = "Email is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = "Enter a valid email address";
      if (Object.keys(errs).length) { setFieldErrors(errs); return; }
      setStep(roleIsLocked ? STEP_PASSWORD : STEP_ROLE);
    } else if (step === STEP_ROLE) {
      if (!role) { setFieldErrors({ role: "Please select your role to continue" }); return; }
      setStep(STEP_PASSWORD);
    }
  };

  const goBack = () => {
    setFieldErrors({});
    setTermsError(false);
    if (step === STEP_ROLE)     setStep(STEP_DETAILS);
    if (step === STEP_PASSWORD) setStep(roleIsLocked ? STEP_DETAILS : STEP_ROLE);
  };

  // ── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setTermsError(false);

    // Honeypot: bots fill hidden fields; real users never touch them
    if (honeypot) return;

    if (!agreedToTerms) { setTermsError(true); return; }

    const result = signUpSchema.safeParse({ firstName, lastName, email, phone: phone || undefined, role, password });
    if (!result.success) {
      const errs: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const f = issue.path[0] as string;
        if (!errs[f]) errs[f] = issue.message;
      }
      setFieldErrors(errs);
      return;
    }

    setLoading(true);
    const fullName    = `${firstName} ${lastName}`;
    const PAID_ROLES  = ["strata_admin", "property_manager", "individual"];
    const isPaidRole  = PAID_ROLES.includes(role as string);
    const emailRedirectTo = inviteToken
      ? `${window.location.origin}/login?redirect=/accept-invite?token=${inviteToken}`
      : checkoutSession
        ? `${window.location.origin}/pricing?checkout_session=${checkoutSession}&plan=${checkoutPlan ?? role}`
        : isPaidRole
          ? `${window.location.origin}/pricing`
          : `${window.location.origin}/dashboard`;

    const { error } = await signUp(email, password, fullName, role as UserRole, emailRedirectTo);
    if (error) {
      setLoading(false);
      toast({ title: "Sign up failed", description: error, variant: "destructive" });
      return;
    }

    trackEvent("user_signed_up", { role: role as string, has_invite: !!inviteToken });

    if (inviteToken) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          await acceptInvitation.mutateAsync({ token: inviteToken, userId: session.user.id });
          toast({ title: "Account created & invitation accepted!", description: `You now have access to ${invitation?.organization?.name ?? "the organization"}.` });
          setLoading(false);
          navigate("/dashboard");
          return;
        }
      } catch { /* fall through to email check */ }
    }

    setRegisteredEmail(email);
    setRegistered(true);
    setLoading(false);
  };

  const isInviteExpired = invitation && new Date(invitation.expires_at) < new Date();
  const strength        = pwStrength(password);
  const selectedRole    = ROLES.find(r => r.value === role);

  // ── Success screen ──────────────────────────────────────────────────────────

  if (registered) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <div className="flex flex-1 items-center justify-center p-4">
          <div className="w-full max-w-sm space-y-8 text-center">
            <Link to="/" className="inline-flex relative items-center justify-center group">
              <div className="absolute inset-0 rounded-3xl bg-primary/15 blur-2xl scale-125" />
              <div className="relative rounded-2xl bg-card border border-border/40 shadow-2xl p-3 backdrop-blur-sm group-hover:scale-[1.03] transition-transform">
                <img src="/logo.png" alt="Stratakey" className="h-16 w-auto object-contain rounded-xl" />
              </div>
            </Link>

            <div className="space-y-3">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 ring-4 ring-primary/10 mx-auto">
                <InboxIcon className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold tracking-tight">Check your inbox</h2>
              <p className="text-sm text-muted-foreground">We sent a confirmation link to</p>
              <p className="text-sm font-semibold break-all">{registeredEmail}</p>
            </div>

            <div className="rounded-xl border bg-muted/40 px-5 py-4 text-left space-y-2.5">
              {[
                "Click the link in the email to verify your account.",
                "After verification you'll be redirected to sign in.",
                "The confirmation link expires in 24 hours.",
              ].map((text) => (
                <div key={text} className="flex items-start gap-2.5 text-xs text-muted-foreground">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                  {text}
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <Button className="w-full" onClick={() => navigate("/login")}>
                <Mail className="mr-2 h-4 w-4" /> Go to Sign In
              </Button>
              <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setRegistered(false)}>
                Didn't receive the email? <span className="text-primary font-medium">Try again</span>
              </button>
            </div>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  // ── Main form ───────────────────────────────────────────────────────────────

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-md space-y-8">

          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 280, damping: 22 }}
            className="flex justify-center"
          >
            <Link to="/" className="relative flex items-center justify-center group">
              <div className="absolute inset-0 rounded-3xl bg-primary/15 blur-2xl scale-125" />
              <div className="relative rounded-2xl bg-card border border-border/40 shadow-2xl p-3 backdrop-blur-sm group-hover:scale-[1.03] transition-transform">
                <img src="/logo.png" alt="Stratakey" className="h-16 w-auto object-contain rounded-xl" />
              </div>
            </Link>
          </motion.div>

          {/* Context banners */}
          {checkoutSession && (
            <Alert className="border-emerald-500/30 bg-emerald-500/5">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <AlertDescription className="text-sm">
                <strong>Payment confirmed!</strong> Create your account below to activate your plan.
              </AlertDescription>
            </Alert>
          )}
          {invitation && !isInviteExpired && (
            <Alert className="border-primary/30 bg-primary/5">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              <AlertDescription className="text-sm">
                You've been invited to join <strong>{invitation.organization?.name}</strong> as a{" "}
                <strong className="capitalize">{invitation.role.replace("_", " ")}</strong>.
              </AlertDescription>
            </Alert>
          )}
          {invitation && isInviteExpired && (
            <Alert variant="destructive">
              <AlertDescription className="text-sm">This invitation has expired. Please request a new one.</AlertDescription>
            </Alert>
          )}

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-2">
            {activeSteps.map((s, i) => (
              <div
                key={s}
                className={`rounded-full transition-all duration-300 ${
                  s === step
                    ? "w-6 h-1.5 bg-primary"
                    : activeSteps.indexOf(step) > i
                      ? "w-1.5 h-1.5 bg-primary"
                      : "w-1.5 h-1.5 bg-border"
                }`}
              />
            ))}
          </div>

          {/* Step heading */}
          <div className="text-center space-y-1">
            <h1 className="text-2xl font-bold tracking-tight">{STEP_META[step].title}</h1>
            <p className="text-sm text-muted-foreground">{STEP_META[step].sub}</p>
          </div>

          {/* ── Step 1: Details ─────────────────────────────────────────────── */}
          {step === STEP_DETAILS && (
            <div className="rounded-xl border bg-card shadow-sm p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">First name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="First"
                      className={`pl-9 h-10 ${fieldErrors.firstName ? "border-destructive" : ""}`}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && goNext()}
                    />
                  </div>
                  <FieldError msg={fieldErrors.firstName} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Last name *</Label>
                  <Input
                    placeholder="Last"
                    className={`h-10 ${fieldErrors.lastName ? "border-destructive" : ""}`}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && goNext()}
                  />
                  <FieldError msg={fieldErrors.lastName} />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    className={`pl-9 h-10 ${fieldErrors.email ? "border-destructive" : ""}`}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={!!invitation}
                    readOnly={!!invitation}
                    onKeyDown={(e) => e.key === "Enter" && goNext()}
                  />
                </div>
                {invitation
                  ? <p className="text-[11px] text-muted-foreground">Email locked to your invitation</p>
                  : <FieldError msg={fieldErrors.email} />
                }
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Phone <span className="text-muted-foreground">(optional)</span></Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="tel"
                    placeholder="+1 (604) 555-0100"
                    className={`pl-9 h-10 ${fieldErrors.phone ? "border-destructive" : ""}`}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && goNext()}
                  />
                </div>
                <FieldError msg={fieldErrors.phone} />
              </div>

              <Button className="w-full h-10 mt-2" onClick={goNext}>
                Continue
              </Button>
            </div>
          )}

          {/* ── Step 2: Role selection ───────────────────────────────────────── */}
          {step === STEP_ROLE && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {ROLES.map(({ value, label, description, Icon, accent }) => {
                  const selected = role === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => { setRole(value); setFieldErrors({}); }}
                      className={`rounded-xl border-2 p-4 text-left transition-all hover:shadow-md active:scale-[0.98] ${
                        selected
                          ? "border-primary bg-primary/5 shadow-sm"
                          : "border-border bg-card hover:border-border/80"
                      }`}
                    >
                      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-lg mb-3 ${accent.split(" ").slice(1).join(" ")}`}>
                        <Icon className={`h-5 w-5 ${accent.split(" ")[0]}`} />
                      </div>
                      <p className="text-sm font-semibold leading-tight">{label}</p>
                      <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{description}</p>
                    </button>
                  );
                })}
              </div>
              {fieldErrors.role && (
                <p className="flex items-center gap-1 text-[11px] text-destructive">
                  <AlertCircle className="h-3 w-3 shrink-0" /> {fieldErrors.role}
                </p>
              )}
              <Button className="w-full h-10" onClick={goNext} disabled={!role}>
                Continue
              </Button>
              <button type="button" onClick={goBack} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mx-auto">
                <ChevronLeft className="h-3.5 w-3.5" /> Back
              </button>
            </div>
          )}

          {/* ── Step 3: Password + Terms ─────────────────────────────────────── */}
          {step === STEP_PASSWORD && (
            <form onSubmit={handleSubmit} noValidate>
              {/* Honeypot — bots fill this; real users never see it */}
              <div aria-hidden="true" style={{ position: "absolute", opacity: 0, height: 0, width: 0, zIndex: -1, pointerEvents: "none", overflow: "hidden" }}>
                <input
                  type="text"
                  name="website"
                  id="website_hp"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              <div className="rounded-xl border bg-card shadow-sm p-6 space-y-4">
                {/* Show locked role as a badge */}
                {roleIsLocked && selectedRole && (
                  <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2">
                    <selectedRole.Icon className={`h-4 w-4 ${selectedRole.accent.split(" ")[0]}`} />
                    <span className="text-xs text-muted-foreground">Signing up as</span>
                    <Badge variant="secondary" className="text-xs capitalize">{selectedRole.label}</Badge>
                  </div>
                )}
                {!roleIsLocked && selectedRole && (
                  <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2">
                    <selectedRole.Icon className={`h-4 w-4 ${selectedRole.accent.split(" ")[0]}`} />
                    <span className="text-xs text-muted-foreground">Role selected:</span>
                    <Badge variant="secondary" className="text-xs">{selectedRole.label}</Badge>
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label className="text-xs">Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type={showPw ? "text" : "password"}
                      placeholder="At least 8 characters"
                      className={`pl-9 pr-10 h-10 ${fieldErrors.password ? "border-destructive" : ""}`}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPw(v => !v)}
                    >
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {password && (
                    <div className="space-y-1 pt-1">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map(i => (
                          <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= strength.score ? strength.color : "bg-muted"}`} />
                        ))}
                      </div>
                      <p className={`text-[11px] font-medium ${strength.score <= 1 ? "text-red-500" : strength.score <= 3 ? "text-amber-500" : "text-green-600"}`}>
                        {strength.label}
                      </p>
                    </div>
                  )}
                  <FieldError msg={fieldErrors.password} />
                </div>

                {/* Terms */}
                <div className="space-y-1.5 pt-1">
                  <label className={`flex items-start gap-3 cursor-pointer ${loading ? "opacity-60 pointer-events-none" : ""}`}>
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => { setAgreedToTerms(e.target.checked); setTermsError(false); }}
                      disabled={loading}
                      className="mt-0.5 h-4 w-4 rounded border-border accent-primary shrink-0"
                    />
                    <span className="text-xs text-muted-foreground leading-relaxed">
                      I agree to the{" "}
                      <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline" onClick={e => e.stopPropagation()}>
                        Terms of Service
                      </Link>
                      {" "}and{" "}
                      <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline" onClick={e => e.stopPropagation()}>
                        Privacy Policy
                      </Link>
                      . I understand my data will be processed in accordance with applicable privacy law.
                    </span>
                  </label>
                  {termsError && (
                    <p className="flex items-center gap-1 text-[11px] text-destructive">
                      <AlertCircle className="h-3 w-3 shrink-0" /> You must accept the Terms to continue.
                    </p>
                  )}
                </div>

                <Button type="submit" className="w-full h-10 mt-1" disabled={loading || !agreedToTerms}>
                  {loading
                    ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating account…</>
                    : inviteToken ? "Create account & accept invitation" : "Create account"
                  }
                </Button>
              </div>

              <button type="button" onClick={goBack} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mx-auto mt-3">
                <ChevronLeft className="h-3.5 w-3.5" /> Back
              </button>
            </form>
          )}

          {/* Footer links */}
          <div className="text-center space-y-1.5 pb-4">
            <p className="text-xs text-muted-foreground">
              Already have an account?{" "}
              <Link
                to={inviteToken ? `/login?redirect=/accept-invite?token=${inviteToken}` : "/login"}
                className="font-medium text-primary hover:underline"
              >
                Sign in
              </Link>
            </p>
            {!inviteToken && (
              <p className="text-xs text-muted-foreground">
                Have an invitation?{" "}
                <Link to="/accept-invite" className="font-medium text-primary hover:underline">
                  Accept invite
                </Link>
              </p>
            )}
          </div>

        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
